import { Logo } from "@/components/Logo";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import * as turf from "@turf/turf";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "next-themes";
import { mapStyleFor } from "@/lib/mapStyle";
import { AppShell } from "@/components/AppShell";
import { RouteSvgOverlay } from "@/components/RouteSvgOverlay";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PaceChart } from "@/components/PaceChart";
import { StravaConnect } from "@/components/StravaConnect";
import { TrainingPlans } from "@/components/TrainingPlans";
import { fmtDistance, fmtLivePace, fmtStoredPace, distUnit } from "@/lib/units";
import { computeElevation } from "@/lib/elevation";
import { toast } from "sonner";
import { Play, Pause, Square, MapPin, ChevronRight, Loader2, Navigation, Save, Trash2, Footprints } from "lucide-react";

const M_TO_FT = 3.28084;
const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
// split pace helper (minutes per mile -> m:ss), used for per-mile split records
const fmtSplitPace = (mi, sec) => {
  if (!mi || mi <= 0) return "0:00";
  const p = (sec / 60) / mi;
  const m = Math.floor(p);
  const s = Math.round((p - m) * 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function Running() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const container = useRef(null);
  const map = useRef(null);
  const watchId = useRef(null);
  const coordsRef = useRef([]);
  const wakeLock = useRef(null);
  const beacon = useRef(null);
  const durationRef = useRef(0);
  const onPositionRef = useRef(null);
  const gpsUnavailableShown = useRef(false); // throttle repeated "signal unavailable" toasts
  const [permission, setPermission] = useState("idle"); // idle|granted|denied
  const [tracking, setTracking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [coords, setCoords] = useState([]); // live route coordinates (drives the SVG polyline overlay)
  const [mapInstance, setMapInstance] = useState(null); // maplibre instance handed to the SVG overlay
  const [distance, setDistance] = useState(0); // miles
  const [duration, setDuration] = useState(0);
  const [elevation, setElevation] = useState(0); // meters (converted to ft on save)
  const [splits, setSplits] = useState([]);
  const [pastRuns, setPastRuns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false); // save/discard/rename dialog after finishing
  const [runTitle, setRunTitle] = useState("Run");
  const pendingRun = useRef(null); // holds the computed run payload awaiting save/discard
  const lastAlt = useRef(null);
  const lastSplitMi = useRef(0);
  const splitStart = useRef(0);
  // Wall-clock timing so duration stays correct even when the screen is off /
  // the tab is backgrounded (browsers throttle/suspend setInterval in that case).
  const runStartTs = useRef(null);     // ms epoch when the run started
  const pausedMs = useRef(0);          // total accumulated paused time (ms)
  const pauseStartTs = useRef(null);   // ms epoch when the current pause began

  // Elapsed active run time in whole seconds, derived from the wall clock.
  const computeElapsedSec = useCallback(() => {
    if (runStartTs.current == null) return 0;
    const pausedSoFar = pausedMs.current + (pauseStartTs.current != null ? Date.now() - pauseStartTs.current : 0);
    return Math.max(0, Math.floor((Date.now() - runStartTs.current - pausedSoFar) / 1000));
  }, []);

  const loadRuns = useCallback(() => {
    api.get("/runs").then(({ data }) => setPastRuns(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadRuns();
    // Handle Strava OAuth return
    const params = new URLSearchParams(window.location.search);
    const strava = params.get("strava");
    if (strava === "connected") {
      toast.success("Strava connected! Importing your runs…");
      setTimeout(loadRuns, 2500);
      window.history.replaceState({}, "", "/running");
    } else if (strava === "error") {
      toast.error("Strava connection failed. Please try again.");
      window.history.replaceState({}, "", "/running");
    }
  }, [loadRuns]);

  // Create the base map once. The route itself is drawn as an SVG overlay (see JSX),
  // so we no longer depend on GL line layers / the style `load` event to render the route.
  useEffect(() => {
    if (map.current || !container.current) return;
    map.current = new maplibregl.Map({
      container: container.current,
      style: mapStyleFor(resolvedTheme),
      center: [-74.006, 40.7128],
      zoom: 14,
      attributionControl: { compact: true },
    });

    // Diagnostic listeners — surface tile/style/worker + WebGL context issues (esp. in production)
    map.current.on("error", (e) => console.error("MAP ERROR", e));
    map.current.on("webglcontextlost", (e) => console.warn("WEBGL LOST", e));
    map.current.on("webglcontextrestored", () => console.log("WEBGL RESTORED"));

    // Expose the instance to the SVG overlay. project() works immediately once the map exists.
    setMapInstance(map.current);

    // Best-effort initial centering on the athlete (not gated on the style `load` event).
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const ll = [pos.coords.longitude, pos.coords.latitude];
          if (map.current) map.current.flyTo({ center: ll, zoom: 15 });
          showBeacon(ll);
        },
        () => {}, { enableHighAccuracy: true }
      );
    }

    return () => { if (map.current) { map.current.remove(); map.current = null; } beacon.current = null; setMapInstance(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let iv;
    // Recompute duration from the wall clock (not a naive +1 counter) so a
    // screen-off / backgrounded interval can't drift. On resume we snap to real time.
    if (tracking && !paused) iv = setInterval(() => setDuration(computeElapsedSec()), 1000);
    return () => clearInterval(iv);
  }, [tracking, paused, computeElapsedSec]);

  const showBeacon = useCallback((lngLat) => {
    if (!map.current) return;
    if (!beacon.current) {
      const el = document.createElement("div");
      el.className = "run-beacon";
      el.setAttribute("data-testid", "run-current-beacon");
      const pulse = document.createElement("span");
      pulse.className = "run-beacon-pulse";
      el.appendChild(pulse);
      beacon.current = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(map.current);
    } else {
      beacon.current.setLngLat(lngLat);
    }
  }, []);

  const onPosition = useCallback((pos) => {
    const { longitude, latitude, altitude, accuracy } = pos.coords;
    const nc = [longitude, latitude];
    const prev = coordsRef.current;
    gpsUnavailableShown.current = false; // got a good fix — allow future error toasts again
    // Always keep the current-position beacon following the athlete, even for lower-accuracy fixes
    showBeacon(nc);
    // Only drop egregiously bad outliers (e.g. >120m) once we already have a track started —
    // this prevents a wild GPS jump from distorting the route without blocking normal fixes.
    if (accuracy != null && accuracy > 120 && prev.length) return;
    if (prev.length) {
      const d = turf.distance(turf.point(prev[prev.length - 1]), turf.point(nc), { units: "meters" });
      if (d < 5) return; // filter GPS jitter (stationary noise)
    }
    const updated = [...prev, nc];
    coordsRef.current = updated;
    setCoords(updated); // drives the SVG route overlay (re-projected on every coord change)
    const line = updated.length > 1 ? turf.lineString(updated) : null;
    const mi = line ? turf.length(line, { units: "miles" }) : 0;
    setDistance(mi);
    if (altitude != null) {
      if (lastAlt.current != null && altitude > lastAlt.current) setElevation((e) => e + (altitude - lastAlt.current));
      lastAlt.current = altitude;
    }
    // splits per mile
    if (Math.floor(mi) > lastSplitMi.current) {
      lastSplitMi.current = Math.floor(mi);
      setSplits((s) => {
        const splitDur = durationRef.current - splitStart.current;
        splitStart.current = durationRef.current;
        return [...s, { mi: lastSplitMi.current, pace: fmtSplitPace(1, splitDur), sec: splitDur }];
      });
    }
    // Keep the camera gently centered on the athlete (short ease, no continuous animation).
    if (map.current) {
      try { map.current.easeTo({ center: nc, zoom: 16, duration: 500 }); }
      catch (err) { console.error("camera ease failed", err); }
    }
  }, [showBeacon]);

  // Keep a ref to the latest onPosition so watchPosition never uses a stale closure
  useEffect(() => { onPositionRef.current = onPosition; }, [onPosition]);
  // Keep durationRef in sync so onPosition doesn't need to be recreated every second
  useEffect(() => { durationRef.current = duration; }, [duration]);

  const requestWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) wakeLock.current = await navigator.wakeLock.request("screen");
    } catch (e) { /* wake lock not critical */ }
  };
  const releaseWakeLock = async () => {
    try { if (wakeLock.current) { await wakeLock.current.release(); wakeLock.current = null; } } catch (e) {}
  };

  // Re-acquire the wake lock if the page becomes visible again mid-run (OS auto-releases it)
  // and immediately snap the displayed duration to real wall-clock time.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && tracking && !paused) {
        requestWakeLock();
        setDuration(computeElapsedSec());
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [tracking, paused, computeElapsedSec]);

  const startWatch = () => {
    if (!navigator.geolocation) { toast.error("GPS is not available on this device/browser."); setTracking(false); return; }
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => onPositionRef.current && onPositionRef.current(pos),
      (err) => {
        // 1 = permission denied, 2 = position unavailable, 3 = timeout
        if (err.code === 1) {
          setPermission("denied");
          toast.error("Location permission denied. Enable location access for this site to track your run.");
          if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
          setTracking(false);
        } else if (err.code === 2) {
          if (!gpsUnavailableShown.current) {
            gpsUnavailableShown.current = true;
            toast.error("GPS signal unavailable. Move to an open area — we'll keep trying.");
          }
        } else if (err.code === 3) {
          // Timeout: keep the watcher alive; it will retry for the next fix.
          console.warn("GPS fix timed out, retrying…");
        } else {
          toast.error("GPS tracking error. Please try again.");
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
  };

  const start = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    if (!window.isSecureContext) { toast.error("GPS tracking requires a secure (HTTPS) connection."); return; }
    coordsRef.current = [];
    setCoords([]);
    setDistance(0); setDuration(0); setElevation(0); setSplits([]);
    lastAlt.current = null; lastSplitMi.current = 0; splitStart.current = 0;
    runStartTs.current = Date.now(); pausedMs.current = 0; pauseStartTs.current = null;
    gpsUnavailableShown.current = false;
    // setCoords([]) above already clears the SVG route overlay for the new run.
    setTracking(true); setPaused(false); setPermission("granted");
    startWatch();
    requestWakeLock();
  };
  const pauseResume = () => {
    if (paused) {
      // Resuming: fold the just-finished pause span into the accumulated paused time.
      if (pauseStartTs.current != null) { pausedMs.current += Date.now() - pauseStartTs.current; pauseStartTs.current = null; }
      startWatch(); requestWakeLock(); setPaused(false);
    } else {
      pauseStartTs.current = Date.now();
      if (watchId.current) navigator.geolocation.clearWatch(watchId.current); releaseWakeLock(); setPaused(true);
    }
  };

  const stop = () => {
    if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
    releaseWakeLock();
    setTracking(false); setPaused(false);
    if (coordsRef.current.length < 2) { toast.info("Run too short to save"); resetRun(); return; }
    const finalDuration = computeElapsedSec();
    const calories = Math.round(distance * 100); // ~100 kcal per mile
    const paceMinPerMi = parseFloat(((finalDuration / 60) / (distance || 1)).toFixed(2));
    // Stash the computed payload; the user chooses to Save (with a name) or Discard.
    pendingRun.current = {
      distance_mi: parseFloat(distance.toFixed(2)),
      duration_sec: finalDuration,
      pace_min_per_mi: paceMinPerMi,
      avg_pace: paceMinPerMi,
      calories,
      elevation_gain_ft: parseFloat((elevation * M_TO_FT).toFixed(0)),
      splits,
      route_geojson: { type: "LineString", coordinates: coordsRef.current },
    };
    const hour = new Date().getHours();
    const partOfDay = hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening";
    setRunTitle(`${partOfDay} Run`);
    setSaveOpen(true);
  };

  const resetRun = () => {
    coordsRef.current = [];
    setCoords([]);
    setDistance(0); setDuration(0); setElevation(0); setSplits([]);
    lastAlt.current = null; lastSplitMi.current = 0; splitStart.current = 0;
    runStartTs.current = null; pausedMs.current = 0; pauseStartTs.current = null;
    pendingRun.current = null;
  };

  const saveRun = async () => {
    if (!pendingRun.current) return;
    setSaving(true);
    try {
      const payload = { ...pendingRun.current, title: runTitle.trim() || "Run" };
      // Compute real terrain elevation in the browser so it works regardless of the
      // backend's outbound-network access. Non-blocking: if it fails, the server falls back.
      try {
        const elev = await computeElevation(payload.route_geojson?.coordinates, payload.distance_mi);
        if (elev) {
          payload.elevation_gain_ft = elev.gain_ft;
          payload.elevation_profile = elev.profile;
        }
      } catch (e) { /* server-side computation will handle it */ }
      const { data } = await api.post("/runs", payload);
      toast.success("Run saved!");
      setSaveOpen(false);
      resetRun();
      navigate(`/runs/${data.id}`);
    } catch (e) {
      toast.error("Could not save run");
    } finally { setSaving(false); }
  };

  const discardRun = () => {
    setSaveOpen(false);
    resetRun();
    toast.info("Run discarded");
  };

  return (
    <AppShell title={tracking ? undefined : "Running"} noPad>
      <div className="relative">
        <div ref={container} data-testid="run-live-map" style={{ height: tracking ? "52vh" : "34vh", width: "100%" }} />
        <RouteSvgOverlay map={mapInstance} coordinates={coords} lineWidth={5} />
        <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 100%)" }} />
        {tracking && (
          <div className="absolute top-3 left-3 right-3 grid grid-cols-3 gap-2">
            {[["Distance", fmtDistance(distance, user), "run-live-distance"],["Time", fmtDur(duration), "run-live-time"],["Pace", fmtLivePace(distance, duration, user), "run-live-pace"]].map(([l,v,t]) => (
              <div key={l} data-testid={t} className="rounded-xl bg-black/55 border border-border backdrop-blur px-2 py-2 text-center">
                <p className="font-mono text-lg font-bold">{v}</p>
                <p className="text-[10px] text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 -mt-6 relative">
        {permission === "idle" && !tracking && (
          <Card className="rounded-2xl bg-card border-border p-4 mb-4">
            <div className="flex items-start gap-3">
              <Navigation className="h-5 w-5 text-[#ccff0c] mt-0.5" />
              <p className="text-sm text-muted-foreground">We use your GPS location <span className="text-foreground">only</span> to track your run route, distance, and pace in <span className="text-[#ccff0c]">{distUnit(user) === "km" ? "kilometers" : "miles"}</span>. Nothing is shared.</p>
            </div>
          </Card>
        )}

        <div className="flex items-center justify-center gap-3 mb-6">
          {!tracking ? (
            <Button data-testid="run-start-button" onClick={start} className="h-14 px-10 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold text-lg rounded-2xl">
              <Play className="h-6 w-6 mr-1" /> Start Run
            </Button>
          ) : (
            <>
              <Button data-testid="run-pause-button" onClick={pauseResume} className="h-14 px-6 bg-secondary border border-border text-foreground rounded-2xl">
                {paused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
              </Button>
              <Button data-testid="run-end-button" onClick={stop} disabled={saving} className="h-14 px-8 bg-red-500/20 border border-red-500/30 text-red-200 rounded-2xl font-semibold">
                {saving ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Square className="h-5 w-5 mr-1" /> Finish</>}
              </Button>
            </>
          )}
        </div>

        {!tracking && (
          <Card
            data-testid="guided-workouts-entry"
            onClick={() => navigate("/run-workouts")}
            className="rounded-2xl bg-[#ccff0c]/10 border border-[#ccff0c]/25 p-4 mb-4 cursor-pointer hover:border-[#ccff0c]/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-[#ccff0c]/15 flex items-center justify-center flex-shrink-0">
                <Footprints className="h-5 w-5 text-[#ccff0c]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold">Guided Run Workouts</p>
                <p className="text-xs text-muted-foreground">50 coach-built sessions — intervals, tempos, hills & more, timed for you.</p>
              </div>
              <ChevronRight className="h-5 w-5 text-[#ccff0c]" />
            </div>
          </Card>
        )}

        {!tracking && (
          <Tabs defaultValue="history">
            <TabsList className="bg-card border border-border w-full mb-4">
              <TabsTrigger value="history" data-testid="run-tab-history" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">History</TabsTrigger>
              <TabsTrigger value="pace" data-testid="run-tab-pace" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Pace Chart</TabsTrigger>
              <TabsTrigger value="plans" data-testid="run-tab-plans" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Plans</TabsTrigger>
            </TabsList>

            <TabsContent value="history">
              <div className="pb-28">
                <StravaConnect onSynced={loadRuns} />
                {pastRuns.length === 0 ? (
                  <div className="flex flex-col items-center py-6">
                    <Logo className="h-16 w-16 rounded-2xl mb-3 opacity-90" />
                    <p className="text-muted-foreground text-sm">No runs yet. Lace up and hit start.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pastRuns.map((r) => (
                      <Card key={r.id} data-testid={`run-history-${r.id}`} onClick={() => navigate(`/runs/${r.id}`)} className="rounded-2xl bg-card border-border p-4 cursor-pointer hover:border-[#ccff0c]/40 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <MapPin className="h-5 w-5 text-[#ccff0c]" />
                            <div>
                              <p className="font-mono font-bold">{fmtDistance(r.distance_mi, user)}</p>
                              <p className="text-xs text-muted-foreground">{r.title ? `${r.title} · ` : ""}{new Date(r.created_at).toLocaleDateString()} · {fmtDur(r.duration_sec)} · {fmtStoredPace(r.avg_pace, user)}</p>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="pace"><PaceChart /></TabsContent>
            <TabsContent value="plans"><TrainingPlans /></TabsContent>
          </Tabs>
        )}
      </div>

      {/* Finish run: rename + save or discard */}
      <Dialog open={saveOpen} onOpenChange={(o) => { if (!o && !saving) setSaveOpen(false); }}>
        <DialogContent className="bg-popover border-border max-w-[420px]" data-testid="run-save-dialog">
          <DialogHeader><DialogTitle className="font-display">Save your run?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {pendingRun.current && (
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Distance", fmtDistance(pendingRun.current.distance_mi, user)], ["Time", fmtDur(pendingRun.current.duration_sec)], ["Pace", fmtStoredPace(pendingRun.current.avg_pace, user)]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-card border border-border py-2">
                    <p className="font-mono text-base font-bold">{v}</p>
                    <p className="text-[10px] text-muted-foreground">{l}</p>
                  </div>
                ))}
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Name this run</label>
              <Input data-testid="run-title-input" value={runTitle} onChange={(e) => setRunTitle(e.target.value)} placeholder="e.g. Morning Run" className="bg-card border-border h-11" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="run-discard-button" onClick={discardRun} disabled={saving} className="flex-1 h-12 bg-red-500/15 text-red-200 border border-red-500/25 hover:bg-red-500/20 font-semibold">
                <Trash2 className="h-4 w-4 mr-1.5" /> Discard
              </Button>
              <Button data-testid="run-save-button" onClick={saveRun} disabled={saving} className="flex-1 h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save Run</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
